import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { clientApi } from '../../lib/clientAuth';
import { useViewMode } from '../../contexts/ViewModeContext';
import { 
  LayoutDashboard, Building2, FolderKanban, 
  LogOut, User, CheckSquare, HelpCircle, Calendar,
  Clock, CheckCircle, AlertTriangle, AlertCircle,
  ChevronLeft, ChevronRight, Filter, TrendingUp,
  FileText, Film, Image, Camera, BarChart3, Archive
} from 'lucide-react';
import ClientTasksPage from '../../components/ClientTasksPage';
import ClientOrganisationPage from '../../components/ClientOrganisationPage';
import ClientHelpDeskPage from '../../components/ClientHelpDeskPage';
import CalendarsPage from '../../components/CalendarsPage';
import NotificationBell from '../../components/NotificationBell';
import { ViewModeToggleSimple } from '../../components/ViewModeToggle';
import MobileHeader from '../../components/MobileHeader';
import MobileBottomNav from '../../components/MobileBottomNav';
import MobileSidebar from '../../components/MobileSidebar';

export default function NewClientDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobileView } = useViewMode();
  
  // Get initial page from URL or default to 'dashboard'
  const initialPage = searchParams.get('page') || 'dashboard';
  const [activeMenu, setActiveMenu] = useState(initialPage);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [clientUser, setClientUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dashboard data
  const [dashboardStats, setDashboardStats] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [clientAnalytics, setClientAnalytics] = useState(null);

  // Filters
  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [filterDepartment, setFilterDepartment] = useState('');

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // Sync activeMenu with URL search params
  useEffect(() => {
    const page = searchParams.get('page');
    if (page && page !== activeMenu) {
      setActiveMenu(page);
    }
  }, [searchParams]);

  // Update URL when activeMenu changes
  const updateUrlWithMenu = (menu) => {
    if (menu === 'dashboard') {
      searchParams.delete('page');
    } else {
      searchParams.set('page', menu);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Handle menu change with URL sync
  const handleMenuChange = (key) => {
    setActiveMenu(key);
    updateUrlWithMenu(key);
  };

  useEffect(() => {
    fetchDashboard();
    fetchStaticData();
  }, []);

  // Refetch dashboard stats when filters change
  useEffect(() => {
    fetchDashboard();
  }, [filterMonth, filterYear, filterDepartment]);

  const fetchDashboard = useCallback(async () => {
    try {
      const params = { month: filterMonth, year: filterYear };
      if (filterDepartment) params.department = filterDepartment;
      const res = await clientApi.get('/api/client/tasks/dashboard', { params });
      setDashboardStats(res.data);
    } catch {
      // Fallback to old stats endpoint
      try {
        const res = await clientApi.get('/api/client/tasks/stats');
        setDashboardStats({
          total_all_time: 0, filtered_total: res.data?.tasks_this_month || 0,
          completed: res.data?.completed_this_month || 0, in_progress: 0,
          new: 0, review: 0, revision: 0,
          overdue: res.data?.overdue || 0, high_priority: res.data?.high_priority_pending || 0,
          content_breakdown: {}, department_breakdown: {},
          filter_month: filterMonth, filter_year: filterYear,
        });
      } catch { /* ignore */ }
    }
    setLoading(false);
  }, [filterMonth, filterYear, filterDepartment]);

  const fetchStaticData = async () => {
    try {
      const [deptsRes, analyticsRes] = await Promise.allSettled([
        clientApi.get('/api/client/departments'),
        clientApi.get('/api/client/analytics')
      ]);
      if (deptsRes.status === 'fulfilled') setDepartments(deptsRes.value.data || []);
      if (analyticsRes.status === 'fulfilled') setClientAnalytics(analyticsRes.value.data || null);
      const storedUser = localStorage.getItem('client_user');
      if (storedUser) setClientUser(JSON.parse(storedUser));
    } catch { /* ignore */ }
  };

  const handlePrevMonth = () => {
    if (filterMonth === 1) { setFilterMonth(12); setFilterYear(filterYear - 1); }
    else setFilterMonth(filterMonth - 1);
  };
  const handleNextMonth = () => {
    if (filterMonth === 12) { setFilterMonth(1); setFilterYear(filterYear + 1); }
    else setFilterMonth(filterMonth + 1);
  };

  const s = dashboardStats || {};
  const completionRate = s.filtered_total > 0 ? Math.round((s.completed / s.filtered_total) * 100) : 0;

  const menuItems = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'tasks', label: 'Tasks', icon: CheckSquare },
    { key: 'calendars', label: 'Calendars', icon: Calendar },
    { key: 'organisation', label: 'Organisation', icon: Building2 },
    { key: 'helpdesk', label: 'Help Desk', icon: HelpCircle }
  ];

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_user');
    navigate('/client/login');
    toast.success('Logged out successfully');
  };

  // Get menu title for mobile header
  const getMenuTitle = () => {
    const item = menuItems.find(m => m.key === activeMenu);
    return item?.label || 'Dashboard';
  };

  // ============================================
  // MOBILE VIEW
  // ============================================
  if (isMobileView) {
    return (
      <div className="min-h-screen bg-[#F7F6F9] dark:bg-[#111111] pb-20" data-testid="client-dashboard-mobile">
        <MobileHeader onMenuOpen={() => setIsSidebarOpen(true)} api={clientApi} portalType="client" title={getMenuTitle()} />
        <MobileSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} menuItems={menuItems}
          activeMenu={activeMenu} setActiveMenu={handleMenuChange} onLogout={handleLogout}
          userName={clientUser?.name || 'Client User'} userRole="Client Portal" portalType="client" />

        <main className="px-3 py-3">
          {activeMenu === 'dashboard' && (
            <div data-testid="client-dashboard-content-mobile">
              <div className="mb-4">
                <h1 className="text-lg font-bold text-[#1a1a2e] dark:text-white">
                  {clientAnalytics?.client_name ? `Hi, ${clientAnalytics.client_name}` : 'Dashboard'}
                </h1>
                <p className="text-xs text-neutral-500">Your tasks and project overview.</p>
              </div>

              {/* Total All-Time Card */}
              <div className="glass-card p-3 mb-3 flex items-center gap-3" data-testid="total-all-time-card-mobile">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <Archive className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-neutral-500">Total Tasks Till Date</p>
                  <p className="text-xl font-bold text-[#1a1a2e] dark:text-white">{s.total_all_time || 0}</p>
                </div>
              </div>

              {/* Mobile Filters */}
              <div className="glass-card p-3 mb-3 flex items-center gap-2 flex-wrap" data-testid="dashboard-filter-bar-mobile">
                <Filter className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                <div className="flex items-center gap-0.5 bg-white dark:bg-white/10 rounded-lg px-1 py-0.5">
                  <button onClick={handlePrevMonth} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-white/10" data-testid="filter-prev-month-mobile">
                    <ChevronLeft className="w-3.5 h-3.5 text-neutral-600 dark:text-neutral-300" />
                  </button>
                  <span className="text-xs font-semibold text-[#1a1a2e] dark:text-white min-w-[80px] text-center">
                    {monthNames[filterMonth - 1]} {filterYear}
                  </span>
                  <button onClick={handleNextMonth} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-white/10" data-testid="filter-next-month-mobile">
                    <ChevronRight className="w-3.5 h-3.5 text-neutral-600 dark:text-neutral-300" />
                  </button>
                </div>
                {(filterMonth !== now.getMonth() + 1 || filterYear !== now.getFullYear() || filterDepartment) && (
                  <button onClick={() => { setFilterMonth(now.getMonth() + 1); setFilterYear(now.getFullYear()); setFilterDepartment(''); }}
                    className="text-[10px] text-pink-600 font-medium" data-testid="filter-reset-mobile">
                    Reset
                  </button>
                )}
              </div>

              {/* Metric Cards - Mobile */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="metric-gradient-blue rounded-2xl p-3" data-testid="metric-filtered-total-mobile">
                  <div className="flex items-center gap-1.5 mb-1"><FileText className="w-3.5 h-3.5 text-white/80" /><p className="text-[10px] text-white/80">Tasks</p></div>
                  <p className="text-lg font-bold text-white">{s.filtered_total || 0}</p>
                </div>
                <div className="metric-gradient-emerald rounded-2xl p-3" data-testid="metric-completed-mobile">
                  <div className="flex items-center gap-1.5 mb-1"><CheckCircle className="w-3.5 h-3.5 text-white/80" /><p className="text-[10px] text-white/80">Completed</p></div>
                  <p className="text-lg font-bold text-white">{s.completed || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-3" data-testid="metric-in-progress-mobile">
                  <div className="flex items-center gap-1.5 mb-1"><Clock className="w-3.5 h-3.5 text-white/80" /><p className="text-[10px] text-white/80">In Progress</p></div>
                  <p className="text-lg font-bold text-white">{s.in_progress || 0}</p>
                </div>
                <div className="metric-gradient-red rounded-2xl p-3" data-testid="metric-overdue-mobile">
                  <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5 text-white/80" /><p className="text-[10px] text-white/80">Overdue</p></div>
                  <p className="text-lg font-bold text-white">{s.overdue || 0}</p>
                </div>
              </div>

              {/* Secondary Stats - Mobile */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="glass-card p-3 text-center" data-testid="metric-new-mobile">
                  <p className="text-[10px] text-neutral-500">New</p>
                  <p className="text-lg font-bold text-[#1a1a2e] dark:text-white">{s.new || 0}</p>
                </div>
                <div className="glass-card p-3 text-center" data-testid="metric-review-mobile">
                  <p className="text-[10px] text-neutral-500">Review</p>
                  <p className="text-lg font-bold text-purple-600">{s.review || 0}</p>
                </div>
                <div className="glass-card p-3 text-center" data-testid="metric-high-priority-mobile">
                  <p className="text-[10px] text-neutral-500">High Priority</p>
                  <p className="text-lg font-bold text-red-600">{s.high_priority || 0}</p>
                </div>
              </div>

              {/* Departments - Mobile */}
              {departments.length > 0 && (
                <div className="glass-card p-4 mb-3" data-testid="client-departments-section-mobile">
                  <h2 className="text-sm font-bold text-[#1a1a2e] dark:text-white mb-3">Departments ({departments.length})</h2>
                  <div className="space-y-2">
                    {departments.map((dept) => (
                      <div key={dept._id || dept.name} className="flex items-center justify-between p-2.5 rounded-xl border border-white/40 dark:border-white/10">
                        <div className="flex items-center gap-2.5">
                          <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <span className="text-sm font-medium text-[#1a1a2e] dark:text-white truncate">{dept.name}</span>
                        </div>
                        <span className="text-xs text-neutral-500">{(s.department_breakdown || {})[dept.name] || 0} tasks</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Completion Rate - Mobile */}
              <div className="glass-card p-4" data-testid="completion-overview-mobile">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-neutral-500">Completion Rate</p>
                  <p className="text-xl font-bold text-[#1a1a2e] dark:text-white">{completionRate}%</p>
                </div>
                <div className="h-2 bg-neutral-100 dark:bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500" style={{ width: `${completionRate}%` }} />
                </div>
                <div className="flex items-center justify-between mt-2 text-[10px] text-neutral-500">
                  <span>{s.completed || 0} completed</span>
                  <span>{s.filtered_total || 0} total</span>
                </div>
              </div>
            </div>
          )}

          {activeMenu === 'tasks' && <ClientTasksPage />}
          {activeMenu === 'calendars' && <CalendarsPage api={clientApi} userType="client" />}
          {activeMenu === 'organisation' && <ClientOrganisationPage />}
          {activeMenu === 'helpdesk' && <ClientHelpDeskPage />}
        </main>

        <MobileBottomNav activeMenu={activeMenu} setActiveMenu={handleMenuChange} onMenuOpen={() => setIsSidebarOpen(true)} portalType="client" />
      </div>
    );
  }

  // ============================================
  // DESKTOP VIEW (Original)
  // ============================================
  return (
    <div className="min-h-screen bg-[#F7F6F9] dark:bg-[#111111] flex" data-testid="client-dashboard-desktop">
      {/* Sidebar — Charcoal + Blue accents */}
      <aside className="w-[260px] bg-[#111111] flex flex-col fixed inset-y-0 left-0 z-20 rounded-r-[28px] shadow-2xl">
        <div className="p-5 pt-7">
          <div className="flex items-center gap-2.5">
            <img src="/hed-icon.png" alt="HED" className="w-7 h-7 rounded-lg" />
            <div className="w-px h-5 bg-white/10" />
            <img src="/girgit-logo.png" alt="Girgit" className="w-7 h-7 rounded-lg" />
            <span className="text-white font-bold text-base tracking-tight">Girgit</span>
          </div>
        </div>

        <div className="px-4 py-3">
          <div className="flex items-center gap-3 bg-white/[0.06] rounded-2xl p-3 border border-white/[0.05]">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-white truncate">{clientUser?.name || 'Client User'}</p>
              <p className="text-[11px] text-white/40">Client Portal</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeMenu === item.key;
            return (
              <button key={item.key} onClick={() => handleMenuChange(item.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl mb-0.5 transition-all duration-200 ${
                  isActive 
                    ? 'bg-blue-500/15 text-blue-400 font-semibold' 
                    : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                }`}
                data-testid={`desktop-menu-${item.key}`}>
                <Icon className="w-4 h-4" /><span className="text-[13px]">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/[0.06]">
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-white/40 hover:bg-white/[0.06] hover:text-[#FF6B8A] rounded-2xl transition-all duration-200"
            data-testid="desktop-logout-btn">
            <LogOut className="w-4 h-4" /><span className="text-[13px]">Logout</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-[260px] overflow-y-auto min-h-screen">
        <header className="sticky top-0 z-40 bg-white/60 dark:bg-[#111111]/80 backdrop-blur-2xl border-b border-white/40 dark:border-white/[0.06] px-8 py-4 flex items-center justify-end gap-3">
          <ViewModeToggleSimple />
          <NotificationBell api={clientApi} portalType="client" accentColor="blue" />
        </header>

        {activeMenu === 'dashboard' && (
          <div className="p-8" data-testid="client-dashboard-content-desktop">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-[#1a1a2e] dark:text-white tracking-tight">
                  {clientAnalytics?.client_name ? `Welcome, ${clientAnalytics.client_name}` : 'Dashboard'}
                </h1>
                <p className="text-sm text-neutral-500 mt-1">Your project overview and task analytics.</p>
              </div>
            </div>

            {/* Permanent Total Card */}
            <div className="glass-card p-5 mb-6 flex items-center justify-between" data-testid="total-all-time-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                  <Archive className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-500">Total Tasks Till Date</p>
                  <p className="text-3xl font-bold text-[#1a1a2e] dark:text-white font-mono-data">{s.total_all_time || 0}</p>
                </div>
              </div>
              <p className="text-xs text-neutral-400">Not affected by filters</p>
            </div>

            {/* Filter Bar */}
            <div className="glass-card p-4 mb-6 flex items-center gap-4 flex-wrap relative z-30" data-testid="dashboard-filter-bar">
              <div className="flex items-center gap-1 text-sm text-neutral-500">
                <Filter className="w-4 h-4" /> Filters:
              </div>
              {/* Month picker */}
              <div className="flex items-center gap-1 bg-white dark:bg-white/10 rounded-xl px-1 py-1">
                <button onClick={handlePrevMonth} className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-white/10" data-testid="filter-prev-month">
                  <ChevronLeft className="w-4 h-4 text-neutral-600" />
                </button>
                <span className="text-sm font-semibold text-[#1a1a2e] dark:text-white min-w-[100px] text-center" data-testid="filter-month-label">
                  {monthNames[filterMonth - 1]} {filterYear}
                </span>
                <button onClick={handleNextMonth} className="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-white/10" data-testid="filter-next-month">
                  <ChevronRight className="w-4 h-4 text-neutral-600" />
                </button>
              </div>
              {/* Department filter */}
              {Object.keys(s.department_breakdown || {}).length > 0 && (
                <select
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e.target.value)}
                  className="text-sm rounded-xl px-3 py-2 bg-white dark:bg-white/10 text-[#1a1a2e] dark:text-white"
                  data-testid="filter-department"
                >
                  <option value="">All Departments</option>
                  {Object.keys(s.department_breakdown || {}).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              )}
              {/* Reset */}
              {(filterMonth !== now.getMonth() + 1 || filterYear !== now.getFullYear() || filterDepartment) && (
                <button onClick={() => { setFilterMonth(now.getMonth() + 1); setFilterYear(now.getFullYear()); setFilterDepartment(''); }}
                  className="text-xs text-pink-600 hover:underline font-medium" data-testid="filter-reset">
                  Reset
                </button>
              )}
            </div>

            {/* Metric Cards Grid */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="metric-gradient-blue rounded-2xl p-5" data-testid="metric-filtered-total">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-white/80" />
                  <p className="text-xs font-semibold text-white/80">Tasks</p>
                </div>
                <p className="text-3xl font-bold text-white font-mono-data">{s.filtered_total || 0}</p>
                <p className="text-xs text-white/60 mt-1">{monthNames[filterMonth - 1]} {filterYear}</p>
              </div>
              <div className="metric-gradient-emerald rounded-2xl p-5" data-testid="metric-completed">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-white/80" />
                  <p className="text-xs font-semibold text-white/80">Completed</p>
                </div>
                <p className="text-3xl font-bold text-white font-mono-data">{s.completed || 0}</p>
                <p className="text-xs text-white/60 mt-1">{completionRate}% rate</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5" data-testid="metric-in-progress">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-white/80" />
                  <p className="text-xs font-semibold text-white/80">In Progress</p>
                </div>
                <p className="text-3xl font-bold text-white font-mono-data">{s.in_progress || 0}</p>
                <p className="text-xs text-white/60 mt-1">Active work</p>
              </div>
              <div className="metric-gradient-red rounded-2xl p-5" data-testid="metric-overdue">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-white/80" />
                  <p className="text-xs font-semibold text-white/80">Overdue</p>
                </div>
                <p className="text-3xl font-bold text-white font-mono-data">{s.overdue || 0}</p>
                <p className="text-xs text-white/60 mt-1">Past due date</p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="glass-card p-5" data-testid="metric-new">
                <p className="text-xs font-medium text-neutral-500 mb-1">New / Pending</p>
                <p className="text-2xl font-bold text-[#1a1a2e] dark:text-white font-mono-data">{s.new || 0}</p>
              </div>
              <div className="glass-card p-5" data-testid="metric-review">
                <p className="text-xs font-medium text-neutral-500 mb-1">In Review</p>
                <p className="text-2xl font-bold text-purple-600 font-mono-data">{s.review || 0}</p>
                <p className="text-[10px] text-neutral-400">Awaiting feedback</p>
              </div>
              <div className="glass-card p-5" data-testid="metric-revision">
                <p className="text-xs font-medium text-neutral-500 mb-1">In Revision</p>
                <p className="text-2xl font-bold text-orange-600 font-mono-data">{s.revision || 0}</p>
                <p className="text-[10px] text-neutral-400">Being revised</p>
              </div>
              <div className="glass-card p-5" data-testid="metric-high-priority">
                <p className="text-xs font-medium text-neutral-500 mb-1">High Priority</p>
                <p className="text-2xl font-bold text-red-600 font-mono-data">{s.high_priority || 0}</p>
                <p className="text-[10px] text-neutral-400">Needs attention</p>
              </div>
            </div>

            {/* Content Breakdown + Departments */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Content Type Breakdown */}
              <div className="glass-card p-6" data-testid="content-breakdown">
                <h3 className="text-sm font-bold text-[#1a1a2e] dark:text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-pink-500" /> Content Breakdown
                </h3>
                {Object.keys(s.content_breakdown || {}).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(s.content_breakdown || {}).map(([type, count]) => {
                      const Icon = type === 'Reel' || type === 'Reels' ? Film : type === 'Post' || type === 'Posts' ? Image : type === 'Story' || type === 'Stories' ? Camera : FileText;
                      const maxCount = Math.max(...Object.values(s.content_breakdown || {}));
                      return (
                        <div key={type} className="flex items-center gap-3">
                          <Icon className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-[#1a1a2e] dark:text-white">{type}</span>
                              <span className="text-xs font-bold text-[#1a1a2e] dark:text-white">{count}</span>
                            </div>
                            <div className="h-1.5 bg-neutral-100 dark:bg-white/10 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" style={{ width: `${(count / maxCount) * 100}%` }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400 text-center py-4">No content this period</p>
                )}
              </div>

              {/* Departments */}
              <div className="glass-card p-6" data-testid="client-departments-section">
                <h3 className="text-sm font-bold text-[#1a1a2e] dark:text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-500" /> Departments
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 ml-auto">{departments.length}</span>
                </h3>
                {departments.length > 0 ? (
                  <div className="space-y-2">
                    {departments.map((dept) => {
                      const taskCount = (s.department_breakdown || {})[dept.name] || 0;
                      return (
                        <div key={dept._id || dept.name} className="flex items-center gap-3 p-3 rounded-xl border border-white/40 dark:border-white/10" data-testid={`dept-card-${dept.name}`}>
                          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#1a1a2e] dark:text-white truncate">{dept.name}</p>
                          </div>
                          <span className="text-xs font-bold text-neutral-500">{taskCount} tasks</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400 text-center py-4">No departments assigned</p>
                )}
              </div>
            </div>

            {/* Completion Overview */}
            <div className="glass-card p-6" data-testid="completion-overview">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-[#1a1a2e] dark:text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-500" /> Completion Overview
                </h3>
                <span className="text-2xl font-bold text-[#1a1a2e] dark:text-white">{completionRate}%</span>
              </div>
              <div className="h-3 bg-neutral-100 dark:bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${completionRate}%` }} />
              </div>
              <div className="flex items-center justify-between mt-3 text-xs text-neutral-500">
                <span>{s.completed || 0} completed</span>
                <span>{s.filtered_total || 0} total in {monthNames[filterMonth - 1]} {filterYear}</span>
              </div>
            </div>
          </div>
        )}

        {activeMenu === 'tasks' && <ClientTasksPage />}
        {activeMenu === 'calendars' && <CalendarsPage api={clientApi} userType="client" />}
        {activeMenu === 'organisation' && <ClientOrganisationPage />}
        {activeMenu === 'helpdesk' && <ClientHelpDeskPage />}
      </main>
    </div>
  );
}
